import { MapPin } from "lucide-react";
import { Section } from "./Section";
import TilePreview from "@/components/TilePreview";

export const MAP_PROVIDERS = {
  osm: {
    label: "OpenStreetMap (darmowy)", needsKey: false,
    tile: () => "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    tileAttr: "© OpenStreetMap contributors", sat: () => "", satAttr: "",
  },
  maptiler: {
    label: "MapTiler (klucz API)", needsKey: true,
    tile: (k) => `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${k}`,
    tileAttr: "© MapTiler © OpenStreetMap contributors",
    sat: (k) => `https://api.maptiler.com/maps/satellite/{z}/{x}/{y}.jpg?key=${k}`,
    satAttr: "© MapTiler © MAXAR",
  },
  mapbox: {
    label: "Mapbox (token)", needsKey: true,
    tile: (k) => `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${k}`,
    tileAttr: "© Mapbox © OpenStreetMap contributors",
    sat: (k) => `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${k}`,
    satAttr: "© Mapbox © MAXAR",
  },
  geoportal: {
    label: "Geoportal — ortofotomapa (PL, bez klucza)", needsKey: false,
    tile: () => "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    tileAttr: "© OpenStreetMap contributors",
    sat: () => "https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMTS/StandardResolution?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ORTOFOTOMAPA&STYLE=default&FORMAT=image/jpeg&TILEMATRIXSET=EPSG:3857&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}",
    satAttr: "© GUGiK / Geoportal.gov.pl — ortofotomapa",
  },
  custom: { label: "Własny serwer / inne (ręcznie)", needsKey: false, manual: true },
};

export function MapSettings({ t, input, mapCfg, setMapCfg, applyMapProvider, open, onToggle }) {
  return (
    <Section icon={MapPin} iconColor="text-emerald-400" title={t("Mapa — dostawca kafli (licencja)")}
      open={open} onToggle={onToggle} testid="settings-sec-map">
      <p className="text-[11px] text-slate-400 mb-3">
        {t("Wybierz dostawcę kafli map. Domyślnie OpenStreetMap (dane ODbL — dozwolone komercyjnie z atrybucją). Dla dostawców komercyjnych wklej klucz API — adresy kafli (mapa + satelita) uzupełnią się automatycznie.")}
      </p>
      <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Dostawca map")}</label>
      <select value={mapCfg.map_provider} onChange={(e) => applyMapProvider(e.target.value, mapCfg.map_api_key)} data-testid="map-provider" className={input}>
        {Object.entries(MAP_PROVIDERS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      {MAP_PROVIDERS[mapCfg.map_provider]?.needsKey && (
        <>
          <label className="text-xs font-semibold text-slate-300 mb-1.5 mt-3 block">{t("Klucz API / token dostawcy")}</label>
          <input value={mapCfg.map_api_key} onChange={(e) => applyMapProvider(mapCfg.map_provider, e.target.value)}
            placeholder={t("wklej klucz API")} data-testid="map-api-key" className={input} autoComplete="off" />
        </>
      )}
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Podgląd mapy (na żywo)")}</label>
        <TilePreview url={mapCfg.map_tile_url} attr={mapCfg.map_tile_attribution} cls="" />
      </div>
      {mapCfg.map_sat_url && (
        <div className="mt-2">
          <label className="text-xs font-semibold text-slate-300 mb-1.5 block">{t("Podgląd satelity (na żywo)")}</label>
          <TilePreview url={mapCfg.map_sat_url} attr={mapCfg.map_sat_attribution} />
        </div>
      )}
      <details className="mt-3">
        <summary className="text-xs text-slate-400 cursor-pointer" data-testid="map-advanced-toggle">{t("Zaawansowane: ręczna edycja adresów")}</summary>
        <div className="mt-2 space-y-2">
          <input value={mapCfg.map_tile_url} onChange={(e) => setMapCfg({ ...mapCfg, map_tile_url: e.target.value })}
            placeholder="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-testid="map-tile-url" className={input} />
          <input value={mapCfg.map_tile_attribution} onChange={(e) => setMapCfg({ ...mapCfg, map_tile_attribution: e.target.value })}
            placeholder="© OpenStreetMap contributors" data-testid="map-tile-attr" className={input} />
          <input value={mapCfg.map_sat_url} onChange={(e) => setMapCfg({ ...mapCfg, map_sat_url: e.target.value })}
            placeholder={t("URL kafli satelity (puste = wyłączone)")} data-testid="map-sat-url" className={input} />
          <input value={mapCfg.map_sat_attribution} onChange={(e) => setMapCfg({ ...mapCfg, map_sat_attribution: e.target.value })}
            placeholder="© dostawca satelity" data-testid="map-sat-attr" className={input} />
        </div>
      </details>
      <p className="text-[11px] text-amber-400/80 mt-3">
        {t("Uwaga: nie wpisuj adresów kafli, na które nie masz licencji komercyjnej (np. serwery Esri/CARTO bez wykupionego planu).")}
      </p>
    </Section>
  );
}
